<!--
SetupFeeOwedBanner — presentational banner for franchises whose setup-fee
obligation is not paid|exempt.

Receives the structured 'setup_fee_owed' keyed banner payload from
useGlobalBanners:
  { key: 'setup_fee_owed', items: [{ franchise_name, franchise_id, status }], severity: 'warn' }

Sibling of GraceBanner / NoSubscriptionBanner. Does not recompute anything —
it only presents the payload useSubscriptionStatus already decided from the
backend's setup_status field.

CTA goes to /pagamento-e-uso (same recovery surface as the other billing
banners) and only for who can self-subscribe.
-->

<template>
  <div
    class="setup-fee-owed-banner"
    data-severity="warn"
    data-testid="setup-fee-owed-banner"
  >
    <i class="setup-fee-owed-banner__icon pi pi-exclamation-circle" />
    <div class="setup-fee-owed-banner__body">
      <strong class="setup-fee-owed-banner__title">
        {{ t('Taxa de implantação pendente') }}
      </strong>

      <span class="setup-fee-owed-banner__message">
        <span
          v-for="item in items"
          :key="item.franchise_id"
          class="setup-fee-owed-banner__item"
        >
          <strong>{{ item.franchise_name }}</strong>
        </span>
      </span>
    </div>
    <AppButton
      v-if="showCta"
      :label="t('Ir para Minhas assinaturas')"
      icon="pi pi-arrow-right"
      icon-pos="right"
      size="small"
      class="setup-fee-owed-banner__cta"
      data-testid="setup-fee-owed-banner-cta"
      @click="router.push(BILLING_RECOVERY_PATH)"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { permissionsState } from '../services/permissionsService'
import { canSelfSubscribe, BILLING_RECOVERY_PATH } from '../composables/useSubscriptionStatus'
import type { GlobalBanner } from '../composables/useGlobalBanners'

interface SetupOwedItem {
  franchise_name: string
  franchise_id: number
  status: string
}

interface SetupFeeOwedBannerPayload extends GlobalBanner {
  items: SetupOwedItem[]
  severity: 'warn'
}

const props = defineProps<{
  banner: SetupFeeOwedBannerPayload
}>()

const { t } = useI18n()
const router = useRouter()
// Optional chained below: the integration mount installs no router, and a
// missing route must read as "not on pagamento-e-uso", not crash.
const route = useRoute()

const items = computed(() => props.banner.items)

// CTA navigates to /pagamento-e-uso — pointless (and confusing) when the
// user is already there; the page itself carries the pay affordances.
const showCta = computed<boolean>(() =>
  canSelfSubscribe(permissionsState.user?.groups) && route?.path !== BILLING_RECOVERY_PATH,
)
</script>

<style scoped>
.setup-fee-owed-banner {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  padding: 0.85rem 1.5rem;
  background: #d97706;
  color: #ffffff;
}

.setup-fee-owed-banner__icon {
  font-size: 1.5rem;
  flex-shrink: 0;
}

.setup-fee-owed-banner__body {
  flex: 1;
  min-width: 240px;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.setup-fee-owed-banner__title {
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.01em;
}

.setup-fee-owed-banner__message {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  font-size: 0.9rem;
}

.setup-fee-owed-banner__item {
  display: inline-flex;
  align-items: center;
  padding: 0.15rem 0.6rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.22);
  white-space: nowrap;
}

.setup-fee-owed-banner__cta {
  flex-shrink: 0;
  font-weight: 700;
}

.setup-fee-owed-banner :deep(.p-button) {
  background: #ffffff;
  border-color: #ffffff;
  color: #b45309;
  font-weight: 700;
}
</style>
